import React, { useState, useEffect } from 'react';
import { EntityType, Attribute, AttributeDataType } from '../types';
import { limsClient } from '../api/limsClient';
import { formatDate } from '../utils/formatDate';
import { Breadcrumb } from './Breadcrumb';
import { ConfirmDialog } from './ConfirmDialog';
import './AttributeManagement.css';

interface AttributeManagementProps {
  entityType: EntityType;
}

interface AttributeFormData {
  name: string;
  data_type: AttributeDataType;
  description: string;
  referenced_entity_type: EntityType | '';
}

export const AttributeManagement: React.FC<AttributeManagementProps> = ({ entityType }) => {
  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingAttribute, setEditingAttribute] = useState<Attribute | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Attribute | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [formData, setFormData] = useState<AttributeFormData>({
    name: '',
    data_type: 'text',
    description: '',
    referenced_entity_type: '',
  });

  const entityTypeDisplay = entityType.charAt(0).toUpperCase() + entityType.slice(1) + 's';

  useEffect(() => {
    loadAttributes();
  }, [entityType]);

  const loadAttributes = async () => {
    try {
      setLoading(true);
      setError(null);
      const attrs = await limsClient.getAttributes(entityType);
      setAttributes(attrs);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load attributes';
      setError(errorMessage);
      console.error('Failed to load attributes:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddClick = () => {
    setFormData({ name: '', data_type: 'text', description: '', referenced_entity_type: '' });
    setEditingAttribute(null);
    setShowAddForm(true);
  };

  const handleEditClick = (attribute: Attribute) => {
    setFormData({
      name: attribute.name,
      data_type: attribute.data_type,
      description: attribute.description || '',
      referenced_entity_type: attribute.referenced_entity_type || '',
    });
    setEditingAttribute(attribute);
    setShowAddForm(true);
  };

  const handleDeleteClick = (attribute: Attribute) => {
    setDeleteTarget(attribute);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const name = deleteTarget.name;
    setDeleteTarget(null);
    try {
      await limsClient.deleteAttribute(entityType, name);
      await loadAttributes();
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Failed to delete attribute';
      setErrorMessage(msg);
      console.error('Failed to delete attribute:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name.trim()) {
      alert('Attribute name is required');
      return;
    }

    try {
      const attribute: Attribute = {
        entity_type: entityType,
        name: formData.name.trim(),
        data_type: formData.data_type,
        description: formData.description.trim() || undefined,
        referenced_entity_type: formData.referenced_entity_type || undefined,
      };

      await limsClient.upsertAttribute(attribute);
      setShowAddForm(false);
      setFormData({ name: '', data_type: 'text', description: '', referenced_entity_type: '' });
      setEditingAttribute(null);
      await loadAttributes();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save attribute';
      alert(`Error: ${errorMessage}`);
      console.error('Failed to save attribute:', err);
    }
  };

  const handleCancel = () => {
    setShowAddForm(false);
    setFormData({ name: '', data_type: 'text', description: '', referenced_entity_type: '' });
    setEditingAttribute(null);
  };

  const dataTypes: AttributeDataType[] = ['text', 'number', 'boolean', 'date', 'json'];

  if (loading) {
    return (
      <div className="attribute-management">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <div className="loading-text">Loading attributes...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="attribute-management">
      <div className="attribute-management-header">
        <Breadcrumb items={[{ label: 'Mariana Labs', path: '/dashboard' }, { label: entityTypeDisplay, path: `/${entityType}` }, { label: 'Attributes' }]} />
        <span className="attr-count-info">{attributes.length} defined</span>
        <button className="btn-add" onClick={handleAddClick}>
          + Add Attribute
        </button>
      </div>

      {error && (
        <div className="error-message">
          Error: {error}
        </div>
      )}

      {showAddForm && (
        <div className="attribute-form-container">
          <form className="attribute-form" onSubmit={handleSubmit}>
            <h2>{editingAttribute ? 'Edit Attribute' : 'Add New Attribute'}</h2>

            <div className="form-group">
              <label htmlFor="name">Attribute Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., sample_id, status, date_created"
                required
                disabled={!!editingAttribute}
              />
              {editingAttribute && (
                <small className="form-hint">Attribute name cannot be changed after creation</small>
              )}
            </div>

            <div className="form-group">
              <label htmlFor="data_type">Data Type *</label>
              <select
                id="data_type"
                value={formData.data_type}
                onChange={(e) => setFormData({ ...formData, data_type: e.target.value as AttributeDataType })}
                required
              >
                {dataTypes.map(dt => (
                  <option key={dt} value={dt}>
                    {dt.charAt(0).toUpperCase() + dt.slice(1)}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="description">Description</label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Optional description of what this attribute represents"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label htmlFor="referenced_entity_type">References Entity Type (optional)</label>
              <select
                id="referenced_entity_type"
                value={formData.referenced_entity_type}
                onChange={(e) => setFormData({ ...formData, referenced_entity_type: e.target.value as EntityType | '' })}
              >
                <option value="">None</option>
                <option value="sample">Sample</option>
                <option value="test">Test</option>
                <option value="assay">Assay</option>
              </select>
              <small className="form-hint">
                Use this when the attribute references another entity (e.g., sample_id, assay_id)
              </small>
            </div>

            <div className="form-actions">
              <button type="submit" className="btn-primary">
                {editingAttribute ? 'Update' : 'Create'} Attribute
              </button>
              <button type="button" className="btn-secondary" onClick={handleCancel}>
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="attributes-table-container">
        {attributes.length === 0 ? (
          <div className="empty-state">
            <p>No attributes defined for {entityTypeDisplay.toLowerCase()}.</p>
            <p>Click "Add Attribute" to create one.</p>
          </div>
        ) : (
          <table className="attributes-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Data Type</th>
                <th>References</th>
                <th>Description</th>
                <th>Created</th>
                <th>Updated</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {attributes.map((attr) => (
                <tr key={attr.id || attr.name}>
                  <td className="attr-name">{attr.name}</td>
                  <td>
                    <span className="data-type-badge" data-type={attr.data_type}>
                      {attr.data_type}
                    </span>
                  </td>
                  <td className="attr-references">
                    {attr.referenced_entity_type ? (
                      <span className="entity-reference-badge">
                        → {attr.referenced_entity_type}
                      </span>
                    ) : (
                      '-'
                    )}
                  </td>
                  <td className="attr-description">{attr.description || '-'}</td>
                  <td className="attr-date">
                    {attr.created_at ? formatDate(attr.created_at) : '-'}
                  </td>
                  <td className="attr-date">
                    {attr.updated_at ? formatDate(attr.updated_at) : '-'}
                  </td>
                  <td className="attr-actions">
                    <button
                      className="btn-edit"
                      onClick={() => handleEditClick(attr)}
                      title="Edit attribute"
                    >
                      Edit
                    </button>
                    <button
                      className="btn-delete"
                      onClick={() => handleDeleteClick(attr)}
                      title="Delete attribute"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {errorMessage && (
        <div className="error-message" style={{ marginTop: '1rem' }}>
          Error: {errorMessage}
          <button onClick={() => setErrorMessage(null)} style={{ marginLeft: '0.5rem', cursor: 'pointer', background: 'none', border: 'none', color: 'inherit' }}>×</button>
        </div>
      )}
      {deleteTarget && (
        <ConfirmDialog
          title="Delete Attribute"
          message={`Are you sure you want to delete the attribute "${deleteTarget.name}"? This will also delete all values for this attribute.`}
          confirmLabel="Delete"
          variant="danger"
          onConfirm={handleDeleteConfirm}
          onCancel={() => setDeleteTarget(null)}
        />
      )}
    </div>
  );
};
